import { useState, useEffect, useContext } from "react";
import { publicFetcher } from "../utils/publicFetcher";
import { AuthContext } from "../context/AuthContext";
import { ACCESS_LEVELS } from "../constants/accessLevels";
import type { VolunteerData } from "../types/volunteer";
import type { AccessLevel } from "../types/common";

interface UseVolunteerDataReturn {
  data: VolunteerData | null;
  loading: boolean;
  error: string | null;
  accessLevel: AccessLevel;
  isOwnProfile: boolean;
}

export const useVolunteerData = (volunteerId: string): UseVolunteerDataReturn => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  
  const [data, setData] = useState<VolunteerData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(ACCESS_LEVELS.PUBLIC);

  const isOwnProfile = isAuthenticated && currentUser?.userId === volunteerId;

  useEffect(() => {
    const fetchVolunteerData = async () => {
      if (!volunteerId) {
        setError("Volunteer ID is required");
        setLoading(false);
        return;
      }

      // If viewing own profile, use data from AuthContext (avoid double fetch)
      if (isOwnProfile && currentUser?.userData) {
        setData(currentUser.userData as VolunteerData);
        setAccessLevel(ACCESS_LEVELS.OWN);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await publicFetcher<VolunteerData>({
          url: `/volunteer/${volunteerId}`,
        });
        
        setData(response);
        
        // Detect access level from response structure
        if ('phone_number' in response || 'permanent_address' in response) {
          setAccessLevel(ACCESS_LEVELS.OWN);
        } else if ('date_of_birth' in response || 'volunteer_rating' in response) {
          setAccessLevel(ACCESS_LEVELS.AUTHENTICATED);
        } else {
          setAccessLevel(ACCESS_LEVELS.PUBLIC);
        }
      } catch (err) {
        console.error("Error fetching volunteer data:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to load volunteer data";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteerData();
  }, [volunteerId, isAuthenticated, currentUser?.userId, currentUser?.userData, isOwnProfile]);

  return { data, loading, error, accessLevel, isOwnProfile };
};

